/**
 * The closed value sets the wire carries, as Zod enums over the domain's own
 * vocabulary.
 *
 * Two mechanisms keep a schema from falling behind the set it describes, and
 * both are compile-time:
 *
 * - Where `@complan/domain` already publishes the values as data - the seven roles
 *   (R1.7), the ten capabilities, the three calendar orientations (R10.2) -
 *   {@link enumSchemaOf} builds the schema straight from that array. Adding a
 *   role in the domain widens the schema with no edit here.
 * - Where the domain publishes the union as a type only, {@link closedSet}
 *   rebuilds the value list under a `Record<TUnion, true>` annotation. A member
 *   the list omits and a member the union does not have are both compile errors,
 *   so the list cannot drift from the union in either direction.
 *
 * Every schema below therefore infers to the domain type itself, not to a
 * look-alike string union: `z.infer<typeof initiativeStatusSchema>` *is*
 * `InitiativeStatus`.
 *
 * One spelling difference is deliberate and belongs to the repository, not here:
 * `complan.assignment_source` stores `manual_override` while the domain calls that
 * value `manual`. The wire follows the domain, and migration 0001's spelling is
 * mapped in `packages/api`.
 */
import { CALENDAR_ORIENTATIONS, CAPABILITIES, COMPLAN_ROLES, } from '@complan/domain';
import { z } from 'zod';
/**
 * A Zod enum over a runtime list of values.
 *
 * `z.enum` needs a non-empty tuple; the domain publishes its closed sets as
 * `readonly T[]`. Throwing on an empty list is right for a module-load-time
 * builder: an empty closed set is a defect in the package that would otherwise
 * surface as an unexplained parse failure on every request.
 */
export function enumSchemaOf(values) {
    const [first, ...rest] = values;
    if (first === undefined) {
        throw new TypeError('A closed value set must hold at least one value.');
    }
    return z.enum([first, ...rest]);
}
/**
 * The members of a string union, as data, checked exhaustively at compile time.
 *
 * `Record<TUnion, true>` demands one key per member, and the excess property
 * check rejects a key the union does not have. Declaration order is preserved,
 * which is what lets the lists below follow the order their requirement states.
 */
export function closedSet(members) {
    return Object.freeze(Object.keys(members));
}
/** The seven roles of R1.7, straight from the domain table. */
export const comPlanRoleSchema = enumSchemaOf(COMPLAN_ROLES);
/** The ten capabilities of the API surface table, straight from the domain table. */
export const capabilitySchema = enumSchemaOf(CAPABILITIES);
/** The three Calendar_View orientations of R10.2. */
export const calendarOrientationSchema = enumSchemaOf(CALENDAR_ORIENTATIONS);
/** `complan.initiative_status`: the six statuses of R9.1, in transition order. */
export const INITIATIVE_STATUSES = closedSet({
    draft: true,
    submitted: true,
    planned: true,
    approved: true,
    final: true,
    cancelled: true,
});
/** See {@link INITIATIVE_STATUSES}. */
export const initiativeStatusSchema = enumSchemaOf(INITIATIVE_STATUSES);
/** `complan.assignment_source`: how the assigned Sales_Cycle set was arrived at (R7.3, R7.4). */
export const ASSIGNMENT_SOURCES = closedSet({ derived: true, manual: true });
/** See {@link ASSIGNMENT_SOURCES}. */
export const assignmentSourceSchema = enumSchemaOf(ASSIGNMENT_SOURCES);
/** `complan.dependency_function`: the two functions of R11.6 and R12.3. */
export const DEPENDENCY_FUNCTIONS = closedSet({ dte: true, csc: true });
/** See {@link DEPENDENCY_FUNCTIONS}. */
export const dependencyFunctionSchema = enumSchemaOf(DEPENDENCY_FUNCTIONS);
/** `complan.ff_role`: role level only, never an employee (R18.13). */
export const FIELD_FORCE_ROLES = closedSet({ ade: true, bde: true });
/** See {@link FIELD_FORCE_ROLES}. */
export const fieldForceRoleSchema = enumSchemaOf(FIELD_FORCE_ROLES);
/**
 * `complan.validation_tier`: the four tiers of R8.1.
 *
 * Declared here rather than derived from the domain because the validation
 * registry (task 5.3) does not exist yet. When it lands, this list becomes a
 * {@link closedSet} over its `Tier` union like the others.
 */
export const VALIDATION_TIERS = Object.freeze([
    'hard',
    'governance',
    'warning',
    'information',
]);
/** See {@link VALIDATION_TIERS}. */
export const validationTierSchema = z.enum(VALIDATION_TIERS);
/**
 * The Initiative_Intake_Form sections of R5.1, which are the field groups a
 * duplication copies (R9.8). The validation summary is not a group: it holds no
 * stored value.
 */
export const INITIATIVE_FIELD_GROUPS = Object.freeze([
    'basic_information_and_ownership',
    'timing_and_sales_cycle',
    'milestones',
    'classification',
    'audience_and_channels',
    'field_force',
    'dte_csc_dependencies',
    'governance_attributes',
]);
/** See {@link INITIATIVE_FIELD_GROUPS}. */
export const initiativeFieldGroupSchema = z.enum(INITIATIVE_FIELD_GROUPS);
/**
 * `complan.run_outcome`: the completion state of a backup run or a restore
 * verification (R17.6, R17.10), as migration 0001 spells it.
 */
export const RUN_OUTCOMES = Object.freeze(['succeeded', 'partial', 'failed']);
/** See {@link RUN_OUTCOMES}. */
export const runOutcomeSchema = z.enum(RUN_OUTCOMES);
/**
 * `complan.audit_event_type`, as migration 0001 defines it.
 *
 * Restated here because the change-history response names the event type and the
 * Detail_Panel renders one row per kind (R12.5, R17.4). Migration 0001 is the
 * source of truth; adding an event type is an `ALTER TYPE` plus an entry here,
 * both of which are deliberate changes rather than incidental ones.
 */
export const AUDIT_EVENT_TYPES = Object.freeze([
    'initiative_created',
    'initiative_field_changed',
    'initiative_duplicated',
    'initiative_deleted',
    'initiative_restored',
    'initiative_content_removed',
    'status_transition',
    'status_transition_rejected',
    'confidentiality_changed',
    'authorized_viewer_added',
    'authorized_viewer_removed',
    'sales_cycle_assignment_changed',
    'sales_cycle_definition_changed',
    'warning_acknowledged',
    'input_request_submitted',
    'master_data_changed',
    'configuration_changed',
    'role_assignment_changed',
    'authentication_failed',
    'authorization_denied',
    'export_requested',
    'export_completed',
    'export_failed',
    'backup_run_recorded',
    'restore_verification_recorded',
    // complan-manpower migration 20 (Requirement 2.9, 3.8, 8.5): Effort_Assumption
    // and Capacity_Period versioning changes, and Recalculation_Run completion.
    'effort_assumption_changed',
    'capacity_period_changed',
    'recalculation_run_completed',
]);
/** See {@link AUDIT_EVENT_TYPES}. */
export const auditEventTypeSchema = z.enum(AUDIT_EVENT_TYPES);
/**
 * The actions a Detail_Panel may offer, gated by capability: every mutation for
 * Commercial_Planning (R12.7), and Input_Request alone for a Read_Only_User
 * (R12.8), who must see no invokable mutation control at all.
 */
export const INITIATIVE_ACTIONS = Object.freeze([
    'edit',
    'duplicate',
    'transition',
    'cancel',
    'delete',
    'restore',
    'change_confidentiality',
    'acknowledge_warning',
    'input_request',
]);
/** See {@link INITIATIVE_ACTIONS}. */
export const initiativeActionSchema = z.enum(INITIATIVE_ACTIONS);
/**
 * The master data sets Master_Data_Administration maintains (R16.1-R16.3), used
 * as the path segment of `/api/admin/master-data/:set`.
 *
 * `region`, `population-unit` and `pos-segment` are Manpower additions
 * (`complan-manpower/requirements.md` Requirement 1, Requirement 2, Requirement
 * 4). All three follow Foundation's exact administration path: `region` and
 * `population-unit` on the shared shape with no extras, `pos-segment` on the
 * shared shape plus `posCount`/`sourceTimestamp`, exactly as `platform` adds a
 * colour - see `complan-manpower/design.md`'s Data Models section, which states
 * `population_unit` "participates in the same inactive-value-marker and
 * reference-protection rules as everything else in Master_Data_Administration."
 */
export const MASTER_DATA_SETS = Object.freeze([
    'platform',
    'channel',
    'customer-group',
    'audience',
    'category',
    'initiative-type',
    'priority',
    'milestone-type',
    'ff-activity-type',
    'region',
    'population-unit',
    'pos-segment',
    // `complan-governance` R7.3: Watchout categories are configurable master data on the
    // same record template, deactivation rule and reference protection as every other set.
    // Migration 26 created the table and widened `count_referencing_initiatives` for it, so
    // adding the literal here is what makes the existing generic screen and the generic
    // `/api/admin/master-data/:set` routes serve it.
    'watchout-category',
    // CSC attributes (Inbound, Outbound, ...) classify an Initiative's CSC involvement.
    // Migration 32 created the table and widened `count_referencing_initiatives` for it.
    'csc-attribute',
]);
/** See {@link MASTER_DATA_SETS}. */
export const masterDataSetSchema = z.enum(MASTER_DATA_SETS);
//# sourceMappingURL=enums.js.map